import axios, { AxiosInstance } from 'axios';
import { normalizeSubscriptionRecord, unwrapSubscriptionList } from '../utils/subscriptionStatus';

// Base API configuration
// Production: https://app-api.spinndo.com/api
// Development: http://localhost:8000/api
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://app-api.spinndo.com/api';

class ApiService {
  private readonly api: AxiosInstance;
  private token: string | null = null;

  constructor() {
    this.api = axios.create({
      baseURL: API_BASE_URL,
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
    });

    // Request interceptor to add auth token and language
    this.api.interceptors.request.use(
      (config) => {
        const token = this.getToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        
        const language = this.getCurrentLanguage();
        if (language) {
          config.headers['Accept-Language'] = language;
          config.headers['X-Locale'] = language;
        }
        
        return config;
      },
      (error) => Promise.reject(new Error(error.message || 'Request failed'))
    );

    // Response interceptor for error handling
    this.api.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          this.clearToken();
          localStorage.removeItem('admin_token');
          globalThis.location.href = '/login';
        }
        return Promise.reject(new Error(error.message || 'Request failed'));
      }
    );
  }

  // Token management
  setToken(token: string): void {
    this.token = token;
    localStorage.setItem('authToken', token);
  }

  getToken(): string | null {
    if (!this.token) {
      this.token = localStorage.getItem('authToken');
    }
    return this.token;
  }

  clearToken(): void {
    this.token = null;
    localStorage.removeItem('authToken');
  }

  // Language management
  getCurrentLanguage(): string {
    // Try to get from cookies first (set by LanguageSelector)
    const cookieLang = document.cookie
      .split('; ')
      .find(row => row.startsWith('lang='))
      ?.split('=')[1];
    
    if (cookieLang) {
      return cookieLang;
    }
    
    // Fallback to localStorage
    const storedLang = localStorage.getItem('i18nextLng');
    if (storedLang) {
      return storedLang;
    }
    
    // Default to English for admin panel
    return 'en';
  }

  // Auth endpoints
  async signIn(email: string, password: string): Promise<any> {
    const response = await this.api.post('/signIn', { email, password });
    
    const payload = response.data || {};
    const success = payload.success === true || payload.status === 'success';
    const token = payload.data?.token || payload.data?.access_token || payload.token || payload.access_token;

    if (success && token) {
      this.setToken(token);
    }

    return {
      success,
      message: payload.message || (success ? 'Operation successful' : 'Operation failed'),
      data: payload.data ?? payload,
    };
  }

  async signUp(email: string, password: string, name: string): Promise<any> {
    return this.api.post('/signUp', { email, password, name });
  }

  async activateCode(email: string, code: string): Promise<any> {
    return this.api.post('/activateCode', { email, code });
  }

  async logout(): Promise<any> {
    const response = await this.api.post('/logout');
    this.clearToken();
    return response.data;
  }

  async getActivityLogs(params: Record<string, string | number | undefined> = {}): Promise<any> {
    const response = await this.api.get('/admin/activity-logs', { params });
    const payload = response.data || {};
    const success = payload.status === 'success' || payload.success === true;
    return {
      success,
      message: payload.message || '',
      data: payload.data ?? payload,
    };
  }

  // Users/Profiles endpoints
  async getProfiles(): Promise<any> {
    const response = await this.api.get('/profiles');
    console.log('API Service: getProfiles response:', response.data);
    const payload = response.data || {};
    const success = payload.success === true || payload.status === 'success';
    return {
      success,
      message: payload.message || (success ? 'Operation successful' : 'Operation failed'),
      data: payload.data ?? payload,
    };
  }

  async getAdmins(): Promise<any> {
    return this.api.get('/admins');
  }

  async getProfileDetails(id: string): Promise<any> {
    return this.api.get(`/profile/${id}`);
  }

  async approveAccount(id: string): Promise<any> {
    const response = await this.api.post('/profile/approve', { 
      user_id: Number.parseInt(id, 10) 
    });
    return response.data;
  }

  async blockProfile(id: string, reason: string = 'Blocked by admin'): Promise<any> {
    const response = await this.api.post('/profile/block', { 
      user_id: Number.parseInt(id, 10), 
      reason: reason 
    });
    return response.data;
  }

  async unblockProfile(id: string): Promise<any> {
    const response = await this.api.post('/profile/unblock', { 
      user_id: Number.parseInt(id, 10) 
    });
    return response.data;
  }

  async updateProfile(id: string, userData: any): Promise<any> {
    const response = await this.api.put(`/profile/${id}`, userData);
    console.log('API Service: updateProfile response:', response.data);
    const payload = response.data || {};
    const success = payload.success === true || payload.status === 'success';
    return {
      success,
      message: payload.message || (success ? 'User updated successfully' : 'Failed to update user'),
      data: payload.data ?? payload,
    };
  }

  async deleteProfile(id: string): Promise<any> {
    const response = await this.api.delete(`/user/${id}`);
    console.log('API Service: deleteProfile response:', response.data);
    const payload = response.data || {};
    const success = payload.success === true || payload.status === 'success';
    return {
      success,
      message: payload.message || (success ? 'User deleted successfully' : 'Failed to delete user'),
      data: payload.data ?? payload,
    };
  }

  // Categories endpoints
  async getCategories(): Promise<any> {
    // Add timestamp to prevent caching
    const timestamp = new Date().getTime();
    const response = await this.api.get(`/categories/getAll?t=${timestamp}`);
    console.log('API Service: getCategories response:', response.data);
    const payload = response.data || {};
    const success = payload.success === true || payload.status === 'success';
    return {
      success,
      message: payload.message || (success ? 'Operation successful' : 'Operation failed'),
      data: payload.data ?? payload,
    };
  }

  async createCategory(categoryData: any): Promise<any> {
    const response = await this.api.post('/categories', categoryData);
    console.log('API Service: createCategory response:', response.data);
    const payload = response.data || {};
    const success = payload.success === true || payload.status === 'success';
    return {
      success,
      message: payload.message || (success ? 'Category created successfully' : 'Failed to create category'),
      data: payload.data ?? payload,
    };
  }

  async updateCategory(id: string, categoryData: any): Promise<any> {
    const response = await this.api.put(`/categories/${id}`, categoryData);
    console.log('API Service: updateCategory response:', response.data);
    const payload = response.data || {};
    const success = payload.success === true || payload.status === 'success';
    return {
      success,
      message: payload.message || (success ? 'Category updated successfully' : 'Failed to update category'),
      data: payload.data ?? payload,
    };
  }

  async deleteCategory(id: string): Promise<any> {
    const response = await this.api.delete(`/categories/${id}`);
    console.log('API Service: deleteCategory response:', response.data);
    const payload = response.data || {};
    const success = payload.success === true || payload.status === 'success';
    return {
      success,
      message: payload.message || (success ? 'Category deleted successfully' : 'Failed to delete category'),
      data: payload.data ?? payload,
    };
  }

  // Countries endpoints
  async getCountries(): Promise<any> {
    const response = await this.api.get('/countries');
    console.log('API Service: getCountries response:', response.data);
    const payload = response.data || {};
    const success = payload.success === true || payload.status === 'success';
    return {
      success,
      message: payload.message || (success ? 'Operation successful' : 'Operation failed'),
      data: payload.data ?? payload,
    };
  }

  async createCountry(countryData: any): Promise<any> {
    return this.api.post('/countries', countryData);
  }

  async updateCountry(id: string, countryData: any): Promise<any> {
    return this.api.put(`/countries/${id}`, countryData);
  }

  async deleteCountry(id: string): Promise<any> {
    return this.api.delete(`/countries/${id}`);
  }

  // Cities endpoints
  async getCities(): Promise<any> {
    const response = await this.api.get('/cities');
    console.log('API Service: getCities response:', response.data);
    const payload = response.data || {};
    const success = payload.success === true || payload.status === 'success';
    return {
      success,
      message: payload.message || (success ? 'Operation successful' : 'Operation failed'),
      data: payload.data ?? payload,
    };
  }

  async createCity(cityData: any): Promise<any> {
    return this.api.post('/cities', cityData);
  }

  async updateCity(id: string, cityData: any): Promise<any> {
    return this.api.put(`/cities/${id}`, cityData);
  }

  async deleteCity(id: string): Promise<any> {
    return this.api.delete(`/cities/${id}`);
  }

  // Packages endpoints
  async getPackages(): Promise<any> {
    const response = await this.api.get('/packages');
    console.log('API Service: getPackages response:', response.data);
    const payload = response.data || {};
    const success = payload.success === true || payload.status === 'success';
    return {
      success,
      message: payload.message || (success ? 'Operation successful' : 'Operation failed'),
      data: payload.data ?? payload,
    };
  }

  async createPackage(packageData: any): Promise<any> {
    return this.api.post('/packages', packageData);
  }

  async updatePackage(id: string, packageData: any): Promise<any> {
    return this.api.put(`/packages/${id}`, packageData);
  }

  async activatePackage(id: string): Promise<any> {
    return this.api.put(`/packages/${id}/activate`);
  }

  async deactivatePackage(id: string): Promise<any> {
    return this.api.put(`/packages/${id}/deactivate`);
  }

  async deletePackage(id: string): Promise<any> {
    return this.api.delete(`/packages/${id}`);
  }


  // Subscriptions endpoints
  async getSubscriptions(): Promise<any> {
    return this.api.get('/subscriptions');
  }

  // Admin Subscription Management endpoints
  async getAllSubscriptions(filters: any = {}): Promise<any> {
    const response = await this.api.get('/admin/subscriptions', { params: filters });
    const payload = response.data || {};
    const success = payload.success === true || payload.status === 'success';
    const list = unwrapSubscriptionList(payload.data).map((item) =>
      normalizeSubscriptionRecord(item as { status?: string; status_label?: string })
    );
    return {
      success,
      message: payload.message || (success ? 'Subscriptions fetched successfully' : 'Failed to fetch subscriptions'),
      data: list,
    };
  }

  async getSubscriptionStatistics(): Promise<any> {
    const response = await this.api.get('/admin/subscriptions/statistics');
    console.log('API Service: getSubscriptionStatistics response:', response.data);
    const payload = response.data || {};
    const success = payload.success === true || payload.status === 'success';
    return {
      success,
      message: payload.message || (success ? 'Subscription statistics fetched successfully' : 'Failed to fetch subscription statistics'),
      data: payload.data || {},
    };
  }

  async getSubscriptionDetails(id: string): Promise<any> {
    const response = await this.api.get(`/admin/subscriptions/${id}`);
    console.log('API Service: getSubscriptionDetails response:', response.data);
    const payload = response.data || {};
    const success = payload.success === true || payload.status === 'success';
    return {
      success,
      message: payload.message || (success ? 'Subscription details fetched successfully' : 'Failed to fetch subscription details'),
      data: payload.data ?? payload,
    };
  }

  async createSubscription(subscriptionData: any): Promise<any> {
    const response = await this.api.post('/admin/subscriptions', subscriptionData);
    console.log('API Service: createSubscription response:', response.data);
    const payload = response.data || {};
    const success = payload.success === true || payload.status === 'success';
    return {
      success,
      message: payload.message || (success ? 'Subscription created successfully' : 'Failed to create subscription'),
      data: payload.data ?? payload,
    };
  }

  async updateSubscriptionStatus(id: string, statusData: any): Promise<any> {
    const response = await this.api.put(`/admin/subscriptions/${id}/status`, statusData);
    const payload = response.data || {};
    const success = payload.success === true || payload.status === 'success';
    const raw = payload.data ?? payload;
    const data =
      raw && typeof raw === 'object' && !Array.isArray(raw)
        ? normalizeSubscriptionRecord(raw as { status?: string; status_label?: string })
        : raw;
    return {
      success,
      message: payload.message || (success ? 'Subscription status updated successfully' : 'Failed to update subscription status'),
      data,
    };
  }

  async disableExpiredServices(): Promise<any> {
    const response = await this.api.post('/admin/subscriptions/disable-expired');
    console.log('API Service: disableExpiredServices response:', response.data);
    const payload = response.data || {};
    const success = payload.success === true || payload.status === 'success';
    return {
      success,
      message: payload.message || (success ? 'Expired services disabled successfully' : 'Failed to disable expired services'),
      data: payload.data ?? payload,
    };
  }

  async deleteSubscription(id: string): Promise<any> {
    const response = await this.api.delete(`/admin/subscriptions/${id}`);
    console.log('API Service: deleteSubscription response:', response.data);
    const payload = response.data || {};
    const success = payload.success === true || payload.status === 'success';
    return {
      success,
      message: payload.message || (success ? 'Subscription deleted successfully' : 'Failed to delete subscription'),
      data: payload.data ?? payload,
    };
  }

  // Notifications endpoints
  async createNotification(notificationData: any): Promise<any> {
    return this.api.post('/notifications', notificationData);
  }

  // Excel endpoints
  async exportProviders(): Promise<Blob> {
    const response = await this.api.get('/admin/excel/export', {
      responseType: 'blob',
    });
    return response.data;
  }

  async downloadProvidersTemplate(): Promise<Blob> {
    const response = await this.api.get('/admin/excel/template', {
      responseType: 'blob',
    });
    return response.data;
  }

  async importProviders(file: File): Promise<any> {
    const formData = new FormData();
    formData.append('file', file);
    return this.api.post('/admin/excel/import', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      timeout: 600000,
    });
  }

  // Account Approval Requests
  async getAccountApprovals(): Promise<any> {
    const response = await this.api.get('/account-approvals');
    console.log('API Service: getAccountApprovals response:', response.data);
    const payload = response.data || {};
    const success = payload.success === true || payload.status === 'success';
    return {
      success,
      message: payload.message || (success ? 'Account approvals fetched successfully' : 'Failed to fetch account approvals'),
      data: payload.data ?? payload,
    };
  }

  async approveAccountRequest(id: string, type: string, notes?: string): Promise<any> {
    const response = await this.api.put(`/account-approvals/${id}/${type}/approve`, { 
      notes 
    });
    console.log('API Service: approveAccountRequest response:', response.data);
    const payload = response.data || {};
    const success = payload.success === true || payload.status === 'success';
    return {
      success,
      message: payload.message || (success ? 'Account request approved successfully' : 'Failed to approve account request'),
      data: payload.data ?? payload,
    };
  }

  async rejectAccountRequest(id: string, type: string, reason: string): Promise<any> {
    const response = await this.api.put(`/account-approvals/${id}/${type}/reject`, { reason });
    console.log('API Service: rejectAccountRequest response:', response.data);
    const payload = response.data || {};
    const success = payload.success === true || payload.status === 'success';
    return {
      success,
      message: payload.message || (success ? 'Account request rejected successfully' : 'Failed to reject account request'),
      data: payload.data ?? payload,
    };
  }

  // Payment Subscriptions
  async getPaymentSubscriptions(): Promise<any> {
    const response = await this.api.get('/payment-subscriptions');
    console.log('API Service: getPaymentSubscriptions response:', response.data);
    const payload = response.data || {};
    const success = payload.success === true || payload.status === 'success';
    return {
      success,
      message: payload.message || (success ? 'Payment subscriptions fetched successfully' : 'Failed to fetch payment subscriptions'),
      data: payload.data ?? payload,
    };
  }

  async publishService(id: string, serviceData: any): Promise<any> {
    const response = await this.api.post(`/payment-subscriptions/${id}/publish`, { service_data: serviceData });
    console.log('API Service: publishService response:', response.data);
    const payload = response.data || {};
    const success = payload.success === true || payload.status === 'success';
    return {
      success,
      message: payload.message || (success ? 'Service published successfully' : 'Failed to publish service'),
      data: payload.data ?? payload,
    };
  }

  async getRenewalNotifications(): Promise<any> {
    const response = await this.api.get('/payment-subscriptions/renewal-notifications');
    console.log('API Service: getRenewalNotifications response:', response.data);
    const payload = response.data || {};
    const success = payload.success === true || payload.status === 'success';
    return {
      success,
      message: payload.message || (success ? 'Renewal notifications fetched successfully' : 'Failed to fetch renewal notifications'),
      data: payload.data ?? payload,
    };
  }

  async markRenewalNotificationSent(id: string): Promise<any> {
    const response = await this.api.put(`/payment-subscriptions/${id}/mark-renewal-sent`);
    console.log('API Service: markRenewalNotificationSent response:', response.data);
    const payload = response.data || {};
    const success = payload.success === true || payload.status === 'success';
    return {
      success,
      message: payload.message || (success ? 'Renewal notification marked as sent' : 'Failed to mark renewal notification'),
      data: payload.data ?? payload,
    };
  }

  // Advertisement Management endpoints
  async getAllAdvertisements(): Promise<any> {
    const response = await this.api.get('/admin/advertisements');
    console.log('API Service: getAllAdvertisements response:', response.data);
    const payload = response.data || {};
    const success = payload.success === true || payload.status === 'success';
    return {
      success,
      message: payload.message || (success ? 'Advertisements fetched successfully' : 'Failed to fetch advertisements'),
      data: payload.data ?? payload,
    };
  }

  async getPendingAdvertisements(): Promise<any> {
    const response = await this.api.get('/admin/advertisements/pending');
    console.log('API Service: getPendingAdvertisements response:', response.data);
    const payload = response.data || {};
    const success = payload.success === true || payload.status === 'success';
    return {
      success,
      message: payload.message || (success ? 'Pending advertisements fetched successfully' : 'Failed to fetch pending advertisements'),
      data: payload.data ?? payload,
    };
  }

  async approveAdvertisement(advertisementId: number | string): Promise<any> {
    const response = await this.api.post('/admin/advertisements/approve', {
      advertisement_id: Number(advertisementId),
    });
    console.log('API Service: approveAdvertisement response:', response.data);
    const payload = response.data || {};
    const success = payload.success === true || payload.status === 'success';
    return {
      success,
      message: payload.message || (success ? 'Advertisement approved successfully' : 'Failed to approve advertisement'),
      data: payload.data ?? payload,
    };
  }

  async rejectAdvertisement(advertisementId: string, rejectionReason: string): Promise<any> {
    const response = await this.api.post('/admin/advertisements/reject', {
      advertisement_id: advertisementId,
      rejection_reason: rejectionReason,
    });
    console.log('API Service: rejectAdvertisement response:', response.data);
    const payload = response.data || {};
    const success = payload.success === true || payload.status === 'success';
    return {
      success,
      message: payload.message || (success ? 'Advertisement rejected successfully' : 'Failed to reject advertisement'),
      data: payload.data ?? payload,
    };
  }

  async addDesign(formData: FormData): Promise<any> {
    const response = await this.api.post('/admin/advertisements/add-design', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      transformRequest: [(data) => data],
    });
    console.log('API Service: addDesign response:', response.data);
    const payload = response.data || {};
    const success = payload.success === true || payload.status === 'success';
    return {
      success,
      message: payload.message || (success ? 'Design added successfully' : 'Failed to add design'),
      data: payload.data ?? payload,
    };
  }

  async getAdvertisementDesigns(advertisementId: string): Promise<any> {
    const response = await this.api.get('/admin/advertisements/designs', {
      params: { advertisement_id: advertisementId },
    });
    console.log('API Service: getAdvertisementDesigns response:', response.data);
    const payload = response.data || {};
    const success = payload.success === true || payload.status === 'success';
    return {
      success,
      message: payload.message || (success ? 'Advertisement designs fetched successfully' : 'Failed to fetch advertisement designs'),
      data: payload.data ?? payload,
    };
  }

  async deleteDesign(designId: string): Promise<any> {
    const response = await this.api.delete(`/admin/advertisements/designs/${designId}`);
    console.log('API Service: deleteDesign response:', response.data);
    const payload = response.data || {};
    const success = payload.success === true || payload.status === 'success';
    return {
      success,
      message: payload.message || (success ? 'Design deleted successfully' : 'Failed to delete design'),
      data: payload.data ?? payload,
    };
  }

  async getAdvertisementStatistics(): Promise<any> {
    const response = await this.api.get('/admin/advertisements/statistics');
    console.log('API Service: getAdvertisementStatistics response:', response.data);
    const payload = response.data || {};
    const success = payload.success === true || payload.status === 'success';
    return {
      success,
      message: payload.message || (success ? 'Advertisement statistics fetched successfully' : 'Failed to fetch advertisement statistics'),
      data: payload.data ?? payload,
    };
  }

  // Create advertisement (Admin)
  async createAdvertisement(formData: FormData): Promise<any> {
    const response = await this.api.post('/admin/advertisements/create', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      transformRequest: [(data) => data],
    });
    console.log('API Service: createAdvertisement response:', response.data);
    const payload = response.data || {};
    const success = payload.success === true || payload.status === 'success';
    return {
      success,
      message: payload.message || (success ? 'Advertisement created successfully' : 'Failed to create advertisement'),
      data: payload.data ?? payload,
    };
  }

  // Update advertisement (Admin)
  async updateAdvertisement(advertisementId: string, formData: FormData): Promise<any> {
    const response = await this.api.post(`/admin/advertisements/update/${advertisementId}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      transformRequest: [(data) => data],
    });
    console.log('API Service: updateAdvertisement response:', response.data);
    const payload = response.data || {};
    const success = payload.success === true || payload.status === 'success';
    return {
      success,
      message: payload.message || (success ? 'Advertisement updated successfully' : 'Failed to update advertisement'),
      data: payload.data ?? payload,
    };
  }

  // Delete advertisement (Admin)
  async deleteAdvertisement(advertisementId: string): Promise<any> {
    const response = await this.api.delete(`/admin/advertisements/delete/${advertisementId}`);
    console.log('API Service: deleteAdvertisement response:', response.data);
    const payload = response.data || {};
    const success = payload.success === true || payload.status === 'success';
    return {
      success,
      message: payload.message || (success ? 'Advertisement deleted successfully' : 'Failed to delete advertisement'),
      data: payload.data ?? payload,
    };
  }
}

// Create and export a singleton instance
const apiService = new ApiService();
export default apiService;
